import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  Card,
  LinearProgress,
  Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LoadingGif from "../../components/LoadingGif";
import { useState, useEffect } from "react";
import { ArrowRight, } from "lucide-react";
import { useHotel } from "../../context/HotelContext";
import { useRoom } from "../../context/RoomContext";

const DashboardTab = ({
  publishedRooms,
  setPublishedRooms,
  todayBookings,
  setTodayBookings,
  reviewBookings,
  setReviewBookings,
  handleOpenModal,
  setCurrentTab,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hotelId } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const [todaysDate, setTodaysDate] = useState("");
  const [day, setDay] = useState(false);
  const { listUserHotels, loading, } = useHotel();
  const { fetchPublishedRooms, ListRoomBookings, } = useRoom();
  const [pendingBookings, setPendingBookings] = useState(0);
  const [todayCheckIn, setTodayCheckIn] = useState(0);
  const [todayCheckOut, setTodayCheckOut] = useState(0);

  useEffect(() => {
    const checkExistingHotels = async () => {
      try {
        const hotels = await listUserHotels();
        if (hotels.length > 0) {
          const published = await fetchPublishedRooms(hotelId);
          setPublishedRooms(published);
          const uniqueRoomIds = [...new Set(published.data.map((room) => room.roomId))];
          const allBookings = [];

          for (const roomId of uniqueRoomIds) {
            const params = { status: "all", page : 1 }; 
            const bookings = await ListRoomBookings(roomId, params);
            allBookings.push(...bookings);
          }

          const today = new Date().toISOString().split('T')[0];

          let checkInCount = 0;
          let checkOutCount = 0;
          let reviewBookings = [];

          const todayFilteredBookings = allBookings.filter((booking) => {
            if (booking.checkIn?.isCheckedIn && booking.checkOut?.isCheckOut) {
              return false;
            }

            const checkInDate = booking.checkIn?.scheduledCheckIn
              ? new Date(booking.checkIn.scheduledCheckIn).toISOString().split('T')[0]
              : null;

            const checkOutDate = booking.checkOut?.scheduledCheckOut
              ? new Date(booking.checkOut.scheduledCheckOut).toISOString().split('T')[0]
              : null;

            if (booking.checkIn.isCheckedIn === false && !booking.review && checkInDate !== today) {
              reviewBookings.push(booking);
            }

            if (checkInDate === today) {
              if (booking.checkIn.isCheckedIn) {
                return false;
              }
              checkInCount++;
              booking.event = "checkIn";
              booking.scheduledTime = new Date(booking.checkIn.scheduledCheckIn).toISOString().split('T')[0];
            }
            if (checkOutDate === today) {
              if (booking.checkIn.isCheckedIn === false) {
                return false;
              }
              checkOutCount++;
              booking.event = "checkOut";
              booking.scheduledTime = new Date(booking.checkOut.scheduledCheckOut).toISOString().split('T')[0];
            }

            return checkInDate === today || checkOutDate === today;
          });

          setTodayBookings(todayFilteredBookings);
          setTodayCheckIn(checkInCount);
          setTodayCheckOut(checkOutCount);
          setReviewBookings(reviewBookings);
          setPendingBookings(reviewBookings.length);
        }
      } catch (error) {
        console.error("Failed to fetch user hotels:", error);
      }
    };

    checkExistingHotels();
  }, [listUserHotels]);


  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    setTodaysDate(formattedDate);
  }, []);

  useEffect(() => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 (Sunday) to 6 (Saturday)
    setDay(currentDay);
  }, []);

  const CalculateOccupancyRate = () => {
    const totalRooms = publishedRooms.total || 0;
    const occupiedRooms = publishedRooms.occupied || 0;
    return totalRooms > 0 ? ((occupiedRooms / totalRooms) * 100).toFixed(2) + "%" : "0%";
  };

  const todayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const OccupancyStatistics = (occupied, available) => {
    if (i18n.language === 'zh') {
      return `已入住 ${(occupied)}間 / 共${(available)}間`;
    }
    return `${(occupied)} of ${(available)} rooms occupied`;
  }

  return (
    loading ? (
      <LoadingGif />
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Top Stats Cards */}
        <Box sx={{ display: "flex", gap: "1%", }}>
          {/* Pending Bookings Card */}
          <Card
            sx={{
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              minHeight: "120px",
              width: "33%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box
                component="img"
                src="/icons/pendingBookings.svg"
                alt="Pending Bookings"
                sx={{ width: "50px", height: "auto" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "20px", color: "#000", fontWeight: 700, mb: 0.5 }}>
                  {t("PendingBookings")}
                </Typography>
                <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#31BACD" }}>
                  {pendingBookings}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#999", mt: 0.5 }}>
                  {t("CompleteWithin48Hours")}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Today Check-Ins Card */}
          <Card
            sx={{
              width: "33%",
              flexDirection: "column",
              gap: 2,
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box
                component="img"
                src="/icons/todayCheckIn.svg"
                alt="Today Check-In"
                sx={{ width: "50px", height: "auto" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "20px", color: "#000", fontWeight: 700, mb: 0.5 }}>
                  {t("TodayCheckIns")}
                </Typography>
                <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#FFB60D" }}>
                  {todayCheckIn}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#999", mt: 0.5 }}>
                  {t("PendingCheckIns")}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Today Check-Outs Card */}
          <Card
            sx={{
              width: "33%",
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box
                component="img"
                src="/icons/todayCheckOut.svg"
                alt="Today Check-Out"
                sx={{ width: "50px", height: "auto" }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "20px", color: "#000", fontWeight: 700, mb: 0.5 }}>
                  {t("TodayCheckOuts")}
                </Typography>
                <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#E5194A" }}>
                  {todayCheckOut}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#999", mt: 0.5 }}>
                  {t("PendingCheckOuts")}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Middle Row: Pending Bookings Table & Room Status */}
        <Box sx={{ display: "flex", gap: "2%", flexDirection: isMobile ? "column" : "row" }}>
          {/* Pending Bookings Table */}
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              width: isMobile ? "100%" : "49%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column", }}>
                <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000", gap: 1, }}>
                  {t("PendingBookings")}
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#999", }}>
                  {t("PleaseReviewBookings")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "18px", fontWeight: "400", color: "#000", }}>
                  {t("ViewAll")}
                </Typography>
                <Button
                  onClick={() => setCurrentTab("BookingList")}
                  sx={{
                    minWidth: "auto",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    backgroundColor: "#31BACD",
                    color: "#fff",
                    padding: 0,
                    alignSelf: isMobile ? "flex-start" : "center",
                    "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  }}
                >
                  <ArrowRight size={18} />
                </Button>
              </Box>
            </Box>

            {/* Mock Data Table */}
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: "300px" }}>
                {pendingBookings !== 0 && (
                  reviewBookings.slice(0, 2).map((booking, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        gap: 2,
                        padding: "12px",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Box
                          component="img"
                          src="/icons/reviewPetImage.svg"
                          alt="Pet"
                          sx={{ width: "26px", height: "auto" }}
                        />
                        <Typography sx={{ fontSize: "16px", color: "#000" }}>
                          {booking.petName}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "14px", color: "#666", whiteSpace: "nowrap" }}>
                        {`${t("Submitted")}: ${new Date(booking.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })}`}
                      </Typography>
                      <Button
                        sx={{
                          fontSize: "14px",
                          padding: "4px 12px",
                          backgroundColor: "#fff",
                          color: "#31BACD",
                          border: "1px solid #31BACD",
                          textTransform: "none",
                          borderRadius: "4px",
                          "&:hover": { backgroundColor: "#BBDEFB" },
                        }}
                        onClick={() => handleOpenModal(booking._id, "review")}
                      >
                        {t("Review")}
                      </Button>
                    </Box>
                  ))
                )}
                {pendingBookings === 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, my: 2, }}>
                    <img src="/icons/noRooms.svg" alt="paw" style={{ width: "16px", height: "auto" }} />
                    <Box sx={{ fontSize: "16px", color: "#D9D9D9", fontWeight: 700, }}>
                      {t('NoPendingBookings')}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>

          {/* Room Status */}
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
              width: isMobile ? "100%" : "49%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
              <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000" }}>
                {t("RoomStatus")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "18px", fontWeight: "400", color: "#000", }}>
                  {t("ViewAll")}
                </Typography>
                <Button
                  onClick={() => setCurrentTab("RoomManagement")}
                  sx={{
                    minWidth: "auto",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    backgroundColor: "#31BACD",
                    color: "#fff",
                    padding: 0,
                    alignSelf: isMobile ? "flex-start" : "center",
                    "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  }}
                >
                  <ArrowRight size={18} />
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: "4%", alignItems: "center", }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "48%", borderRight: "1px solid #AEAEB2", paddingRight: "20px" }}>
                {/* Room Available */}
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box
                      component="img"
                      src="/icons/roomAvailable.svg"
                      alt="Available"
                      sx={{ width: "32px", height: "auto" }}
                    />
                    <Typography sx={{ fontSize: "16px", color: "#000", }}>
                      {t("Available")}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "16px", color: "#000" }}>
                    {publishedRooms.available || 0}
                  </Typography>
                </Box>

                {/* Room Reserved */}
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box
                      component="img"
                      src="/icons/roomReserved.svg"
                      alt="Reserved"
                      sx={{ width: "32px", height: "auto" }}
                    />
                    <Typography sx={{ fontSize: "16px", color: "#000", }}>
                      {t("Reserved")}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "16px", color: "#000" }}>
                    {publishedRooms.reserved || 0}
                  </Typography>
                </Box>

                {/* Room Occupied */}
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box
                      component="img"
                      src="/icons/roomOccupied.svg"
                      alt="Occupied"
                      sx={{ width: "32px", height: "auto" }}
                    />
                    <Typography sx={{ fontSize: "16px", color: "#000", }}>
                      {t("Occupied")}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "16px", color: "#000" }}>
                    {publishedRooms.occupied || 0}
                  </Typography>
                </Box>
              </Box>

              {/* Occupancy Rate */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "48%",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#000", }}>
                  {t("OccupancyRate")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: "28px", fontWeight: "700", color: "#000" }}>
                      {CalculateOccupancyRate()}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#000" }}>
                      {OccupancyStatistics(publishedRooms.occupied || 0, publishedRooms.total || 0)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={CalculateOccupancyRate().replace("%", "")}
                    sx={{
                      height: "6px",
                      width: "100%",
                      borderRadius: "3px",
                      backgroundColor: "#F0F0F0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#FF6B35",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Today's Arrivals Table */}
        <Box sx={{ display: "flex", gap: 2, flexDirection: isMobile ? "column" : "row" }}>

          {/* Today's Arrivals Table */}
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
              padding: "20px",
              borderRadius: "10px",
              border: "1.5px solid #E0E0E0",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E0E0E0", paddingBottom: "10px" }}>
              <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000" }}>
                {t("TodaysArrivals")}
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#4A5565" }}>
                {todaysDate + ", " + t(todayNames[day])}
              </Typography>
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Box
                sx={{
                  minWidth: "100%",
                  display: "grid",
                  alignItems: "center",
                  gridTemplateColumns: "repeat(8, 1fr)",
                  gap: 1,
                  fontSize: "14px",
                }}
              >
                {/* Header */}
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center", }}>{t("PetName")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("Owner")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("BookingNo")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("Contact")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("ScheduleTime")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("Room")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("Event")}</Typography>
                <Typography sx={{ fontWeight: "700", color: "#000", textAlign: "center" }}>{t("Action")}</Typography>

                {/* Data Rows */}
                {(todayCheckIn !== 0 || todayCheckOut !== 0) && (
                  todayBookings.map((event) => (
                    <>
                      <Typography sx={{ color: "#666", textAlign: "center" }}>{event.petName}</Typography>
                      <Typography sx={{ color: "#666", textAlign: "center" }}>{event.name}</Typography>
                      <Typography sx={{ color: "#666", textAlign: "center" }}>{event.bookingNo}</Typography>
                      <Typography sx={{ color: "#666", textAlign: "center" }}>{event.phoneNumber}</Typography>
                      <Typography sx={{ color: "#666", textAlign: "center" }}>{event.scheduledTime}</Typography>
                      <Typography sx={{ color: "#666", textAlign: "center" }}>{event.roomName}</Typography>
                      <Chip
                        label={t(event.event === "checkIn" ? "CheckIn" : "CheckOut")}
                        size="large"
                        sx={{
                          backgroundColor: event.event === "checkIn" ? "#FFB60D" : "#E5194A",
                          color: "#fff",
                          fontSize: "12px",
                          height: "25px",
                          width: "90px",
                          margin: "0 auto",
                        }}
                      />
                      <Button
                        sx={{
                          fontSize: "14px",
                          padding: "4px 8px",
                          margin: "0 auto",
                          backgroundColor: "#fff",
                          color: "#31BACD",
                          border: "1px solid #31BACD",
                          textTransform: "none",
                          borderRadius: "4px",
                          width: "90px",
                          "&:hover": { backgroundColor: "#BBDEFB" },
                        }}
                        onClick={() => handleOpenModal(event._id, event.event)}
                      >
                        {event.event === "checkIn" ? t("CheckInAction") : t("CheckOutAction")}
                      </Button>
                    </>
                  ))
                )}
              </Box>
              {todayCheckIn === 0 && todayCheckOut === 0 && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, my: 5, }}>
                  <img src="/icons/noRooms.svg" alt="paw" style={{ width: "16px", height: "auto" }} />
                  <Box sx={{ fontSize: "16px", color: "#D9D9D9", fontWeight: 700, }}>
                    {t('NoCheckInCheckOutToday')}
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    )
  );
};

export default DashboardTab;
